import {
  useCancelSubscription,
  useCreateSetupIntent,
  useGetSubscription,
  useListBillingPlans,
  useListCreditUsagePerUser,
  useListPaymentMethods,
  useListStripeInvoices,
  useUpdateSubscription,
} from "@/api/stripe";
import { FreeTrialBanner } from "@/components/billing/FreeTrialBanner";
import { UsageProgressBar } from "@/components/billing/UsageProgressBar";
import { UserProgressBar } from "@/components/billing/UserProgressBar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ROUTES } from "@/constants/routes";
import { useBetaPlan } from "@/hooks/useBetaPlan";
import { useEnterprisePlan } from "@/hooks/useEnterprisePlan";
import { useIsSelfServeBilling } from "@/hooks/useIsSelfServeBilling";
import { useStartPaymentMethodSetup } from "@/hooks/useStartPaymentMethodSetup";
import {
  trackBillingBudgetUpdated,
  trackBillingEmailUpdated,
  trackBillingInvoiceDownloaded,
  trackBillingInvoiceViewed,
  trackBillingPaymentMethodUpdateStarted,
  trackBillingPlanChangeClicked,
  trackBillingPlanConfigured,
  trackBillingPlanSelected,
  trackBillingSubscriptionCancelled,
  trackBillingSubscriptionReactivated,
} from "@/utils/analytics";
import { formatCurrencyFromCents, formatInteger } from "@/utils/currency";
import { buildPlanChangeAction } from "@/utils/planChange";
import { route } from "@/utils/route";
import type { UsageRecordSumResult } from "@cohesive/api-client";
import { getAvatarImage, getInitials } from "@cohesive/ui-components";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Activity,
  AlertTriangle,
  Coins,
  CreditCard,
  Download,
  ExternalLink,
  Settings,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate } from "react-router";
import { PaymentIcon } from "react-svg-credit-card-payment-icons";
import { z } from "zod";
import { PlanConfigurationModal } from "../../billing/PlanConfigurationModal";
import type { PlanSelection, TransformedPlan } from "../../billing/PlansPage";

// Types
interface PaymentMethodDisplay {
  icon?: React.ComponentType<{
    width?: number;
    height?: number;
  }>;
  text: string;
  cardType?: string;
}

// Zod schema for email validation
const billingEmailSchema = z.object({
  billing_email: z.string().email("Please enter a valid email address"),
});

type BillingEmailFormData = z.infer<typeof billingEmailSchema>;

export const UsageBilling: React.FC = () => {
  const navigate = useNavigate();
  const { data: subscription, isLoading: isSubscriptionLoading } =
    useGetSubscription();
  const { data: paymentMethods, isLoading: isPaymentMethodsLoading } =
    useListPaymentMethods();
  const { data: billingPlans } = useListBillingPlans();
  const { data: creditUsage, isLoading: isCreditUsageLoading } =
    useListCreditUsagePerUser();
  const { data: invoices, isLoading: isInvoicesLoading } =
    useListStripeInvoices();
  const createSetupIntent = useCreateSetupIntent();
  const cancelSubscription = useCancelSubscription();
  const updateSubscription = useUpdateSubscription();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showReactivateDialog, setShowReactivateDialog] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<TransformedPlan | null>(
    null,
  );
  const isEnterprisePlan = useEnterprisePlan();
  const isBetaPlan = useBetaPlan();
  const isSelfServeBilling = useIsSelfServeBilling();
  const startPaymentMethodSetup = useStartPaymentMethodSetup();

  // Budget state
  const [budgetAmount, setBudgetAmount] = useState<string>("");
  const [isUnlimitedBudget, setIsUnlimitedBudget] = useState<boolean>(false);
  const [isBudgetSaving, setIsBudgetSaving] = useState(false);

  // Extract subscription details
  const currentPlan = subscription?.data.stripe_subscription;

  // Billing email dialog state
  const [showBillingEmailDialog, setShowBillingEmailDialog] = useState(false);

  // Form for billing email
  const billingEmailForm = useForm<BillingEmailFormData>({
    resolver: zodResolver(billingEmailSchema),
    defaultValues: {
      billing_email: currentPlan?.billing_email || "",
    },
  });
  const currentPlanName = currentPlan?.plan_name;
  const currentPlanDisplayName = currentPlan?.plan_display_name;
  const subscriptionStatus = currentPlan?.status;
  const billingInterval = currentPlan?.billing_interval;
  const seatCount = currentPlan?.seat_count || 1;
  const seatPrice = currentPlan?.seat_price || 0;
  const includedCreditsPerSeat = currentPlan?.included_credits_per_seat || 0;
  const creditPrice = currentPlan?.credit_price || 0; // Price in cents per credit
  const maxOverageSpendPerPeriod =
    currentPlan?.max_credit_overage_spend_per_period || 0;

  // Check if user is on free trial (for badge display)
  const isOnFreeTrial = subscriptionStatus === "trialing";

  // Check if subscription is scheduled for cancellation (has cancel_at date set)
  const cancelAtDate = currentPlan?.cancel_at;
  const isScheduledForCancellation =
    !!cancelAtDate && subscriptionStatus !== "canceled";

  // Format the cancellation date
  const formattedCancelDate = cancelAtDate
    ? new Date(cancelAtDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  // Get next billing date
  const nextBillingDate = currentPlan?.current_period_end;
  const formattedBillingDate = nextBillingDate
    ? new Date(nextBillingDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  // Find default payment method
  const defaultPaymentMethod = paymentMethods?.data.payment_methods?.find(
    (pm) => pm.default,
  );

  // Extract payment method details based on type
  const paymentMethodType = defaultPaymentMethod?.object?.type;
  const isCard = paymentMethodType === "card";
  const isBankAccount = paymentMethodType === "us_bank_account";

  const lastFourDigits = isCard
    ? defaultPaymentMethod?.object?.card?.last4
    : isBankAccount
      ? defaultPaymentMethod?.object?.us_bank_account?.last4
      : null;

  const cardBrand = isCard ? defaultPaymentMethod?.object?.card?.brand : null;
  const bankName = isBankAccount
    ? defaultPaymentMethod?.object?.us_bank_account?.bank_name
    : null;

  // Helper function to get payment method display info
  const getPaymentMethodDisplay = (): PaymentMethodDisplay | null => {
    if (!defaultPaymentMethod) return null;

    if (isCard && cardBrand && lastFourDigits) {
      // Map Stripe brand names to package card types (note: package expects capitalized types)
      const mapCardBrand = (
        brand: string,
      ):
        | "Visa"
        | "Mastercard"
        | "Amex"
        | "Discover"
        | "Jcb"
        | "Diners"
        | "Unionpay"
        | "Maestro"
        | "Generic" => {
        const brandMap: Record<
          string,
          | "Visa"
          | "Mastercard"
          | "Amex"
          | "Discover"
          | "Jcb"
          | "Diners"
          | "Unionpay"
          | "Maestro"
          | "Generic"
        > = {
          visa: "Visa",
          mastercard: "Mastercard",
          amex: "Amex",
          american_express: "Amex",
          discover: "Discover",
          jcb: "Jcb",
          diners: "Diners",
          diners_club: "Diners",
          unionpay: "Unionpay",
          maestro: "Maestro",
        };
        return brandMap[brand.toLowerCase()] || "Generic";
      };

      const cardType = mapCardBrand(cardBrand);

      // Create a wrapper component for PaymentIcon
      const CardIcon = ({
        width = 30,
      }: {
        width?: number;
        height?: number;
      }) => <PaymentIcon type={cardType} format="flatRounded" width={width} />;

      return {
        icon: CardIcon,
        text: `•••• •••• •••• ${lastFourDigits}`,
        cardType,
      };
    }

    if (isBankAccount && bankName && lastFourDigits) {
      return {
        text: `${bankName} •••• ${lastFourDigits}`,
      };
    }

    return {
      icon: CreditCard,
      text: "Unknown payment method type",
    };
  };

  const paymentMethodDisplay = getPaymentMethodDisplay();

  // Transform billing plans data (similar to PlansPage)
  // Update form when subscription data changes
  useEffect(() => {
    if (currentPlan?.billing_email) {
      billingEmailForm.reset({
        billing_email: currentPlan.billing_email,
      });
    }
  }, [currentPlan?.billing_email, billingEmailForm]);

  // Initialize budget from current subscription
  useEffect(() => {
    if (currentPlan) {
      const currentBudgetCents = maxOverageSpendPerPeriod;

      if (currentBudgetCents === -1) {
        // Unlimited budget
        setIsUnlimitedBudget(true);
        setBudgetAmount("");
      } else {
        // Specific budget amount (including $0)
        setIsUnlimitedBudget(false);
        const currentBudgetDollars = currentBudgetCents / 100;
        setBudgetAmount(
          currentBudgetCents >= 0 ? currentBudgetDollars.toString() : "",
        );
      }
    }
  }, [currentPlan, maxOverageSpendPerPeriod]);

  // Calculate equivalent credits for budget amount
  const budgetAmountNumber =
    budgetAmount === "" ? 0 : parseFloat(budgetAmount) || 0;
  const budgetCredits =
    creditPrice > 0 ? Math.floor((budgetAmountNumber * 100) / creditPrice) : 0;

  const handleSaveBudget = async () => {
    if (!currentPlan || !currentPlanName || !billingInterval) {
      toast.error("Unable to update budget. Missing subscription information.");
      return;
    }

    try {
      setIsBudgetSaving(true);

      let budgetValue: number | undefined;

      if (isUnlimitedBudget) {
        budgetValue = -1; // -1 represents unlimited budget
      } else if (budgetAmountNumber >= 0) {
        budgetValue = Math.round(budgetAmountNumber * 100); // Convert dollars to cents (including $0)
      } else {
        budgetValue = undefined; // No budget set
      }

      await updateSubscription.mutateAsync({
        plan_name: currentPlanName,
        billing_interval: billingInterval,
        seat_count: seatCount,
        max_credit_overage_spend_per_period: budgetValue,
      } as any); // Type assertion to work around potential type mismatch

      // Track budget update
      trackBillingBudgetUpdated({
        budgetAmount: isUnlimitedBudget ? undefined : budgetAmountNumber,
        isUnlimited: isUnlimitedBudget,
        planName: currentPlanName,
      });

      toast.success("Budget updated successfully");
    } catch (error: any) {
      toast.error(
        error.message || "Failed to update budget. Please try again.",
      );
    } finally {
      setIsBudgetSaving(false);
    }
  };

  const handleUpdateBillingEmail = async (data: BillingEmailFormData) => {
    if (!currentPlan || !currentPlanName || !billingInterval) {
      toast.error(
        "Unable to update billing email. Missing subscription information.",
      );
      return;
    }

    try {
      await updateSubscription.mutateAsync({
        plan_name: currentPlanName,
        billing_interval: billingInterval,
        seat_count: seatCount,
        billing_email: data.billing_email,
      } as any);

      // Track billing email update
      trackBillingEmailUpdated({
        planName: currentPlanName,
      });

      toast.success("Billing email updated successfully");
      setShowBillingEmailDialog(false);
    } catch (error: any) {
      toast.error(
        error.message || "Failed to update billing email. Please try again.",
      );
    }
  };

  const transformedPlans = useMemo(() => {
    if (!billingPlans?.data.billing_plans) return [];

    const planMap = new Map<string, TransformedPlan>();

    billingPlans.data.billing_plans.forEach((plan) => {
      const key = plan.name;
      if (!planMap.has(key)) {
        planMap.set(key, {
          name: plan.name,
          display_name: plan.display_name,
          credit_price: plan.credit_price,
          included_credits_per_seat: plan.included_credits_per_seat,
          available_intervals: [],
        });
      }

      const transformed = planMap.get(key)!;
      if (plan.billing_interval === "month") {
        transformed.monthly_price = plan.seat_price / 100; // Convert from cents
        transformed.available_intervals.push("month");
      } else if (plan.billing_interval === "year") {
        transformed.yearly_price = plan.seat_price / 100; // Convert from cents
        transformed.available_intervals.push("year");
      }
    });

    return Array.from(planMap.values());
  }, [billingPlans]);

  const handleUpgradePlan = () => {
    // Find current plan or default to first available plan
    const planToShow =
      transformedPlans.find((p) => p.name === currentPlanName) ||
      transformedPlans[0];
    if (planToShow) {
      trackBillingPlanSelected({
        planName: planToShow.name,
        source: "usage_billing",
      });
      setSelectedPlan(planToShow);
      setShowPlanModal(true);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    try {
      // Track payment method update start
      trackBillingPaymentMethodUpdateStarted({
        source: "usage_billing",
        currentPlan: currentPlanName,
      });

      // Delegate to the shared payment-method setup so this page stays in sync
      // with the banner flow. keepTrial preserves the remaining trial days when
      // a card is added/updated mid-trial (otherwise ConfirmSetupPage would set
      // trial_end_now and charge immediately).
      startPaymentMethodSetup({
        redirectTo: ROUTES.SETTINGS.USAGE_BILLING,
        keepTrial: true,
      });
    } catch (error) {
      toast.error("Failed to start payment method setup. Please try again.");
    }
  };

  const handleCancelSubscription = async () => {
    try {
      await cancelSubscription.mutateAsync(true);

      // Track subscription cancellation
      trackBillingSubscriptionCancelled({
        planName: currentPlanName,
        billingInterval,
        seatCount,
      });

      setShowCancelDialog(false);
      toast.success(
        "Subscription cancelled successfully. You'll continue to have access until your current billing period ends.",
      );
    } catch (error: any) {
      toast.error(
        error.message || "Failed to cancel subscription. Please try again.",
      );
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      await cancelSubscription.mutateAsync(false);

      // Track subscription reactivation
      trackBillingSubscriptionReactivated({
        planName: currentPlanName,
        billingInterval,
        seatCount,
      });

      setShowReactivateDialog(false);
      toast.success(
        "Subscription reactivated successfully. Your subscription will continue beyond the current billing period.",
      );
    } catch (error: any) {
      toast.error(
        error.message || "Failed to reactivate subscription. Please try again.",
      );
    }
  };

  const handlePlanConfigurationComplete = async (selection: PlanSelection) => {
    const hasPaymentMethod =
      paymentMethods?.data.payment_methods?.some((pm) => pm.default) || false;

    // Track plan configuration
    trackBillingPlanConfigured({
      planName: selection.plan.name,
      billingInterval: selection.billing_cycle,
      seatCount: selection.user_count,
      hasPaymentMethod,
    });

    setShowPlanModal(false);

    const planChange = buildPlanChangeAction({
      selection,
      subscription: subscription?.data.stripe_subscription,
      paymentMethods: paymentMethods?.data.payment_methods,
    });

    if (planChange.type === "update") {
      // Update subscription directly
      try {
        await updateSubscription.mutateAsync(planChange.payload);
        toast.success("Plan updated successfully");
      } catch (error: any) {
        toast.error(error.message || "Failed to update plan");
      }
    } else {
      // Navigate to checkout with redirect back to usage billing
      const checkoutParams: Record<string, string> = {
        plan: selection.plan.name,
        billing_cycle: selection.billing_cycle,
        user_count: String(selection.user_count),
        redirect_to: ROUTES.SETTINGS.USAGE_BILLING,
      };

      navigate(route(ROUTES.BILLING.CHECKOUT, {}, checkoutParams));
    }
  };

  return (
    <>
      <title>Usage & Billing</title>

      <div className="space-y-8">
        {/* Free Trial Alert */}
        <FreeTrialBanner
          subscription={subscription}
          onManageBillingClick={handleUpgradePlan}
        />

        {/* Current Plan Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-col lg:flex-row gap-3">
              <div className="flex items-start gap-3 w-full">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-lc-active/10 to-rust/10 flex items-center justify-center">
                  <CreditCard className="h-6 w-6 text-lc-active" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3
                      data-testid="current-plan-name"
                      className="text-lg font-semibold"
                    >
                      {currentPlanDisplayName || "Custom"} Plan
                    </h3>

                    {isOnFreeTrial && (
                      <Badge
                        variant="secondary"
                        className="bg-spectrum-orange-light/30 text-lc-warning border-lc-warning/30"
                      >
                        Free Trial
                      </Badge>
                    )}
                    {subscriptionStatus === "canceled" && (
                      <Badge variant="destructive">Cancelled</Badge>
                    )}
                    {isScheduledForCancellation && (
                      <Badge
                        variant="secondary"
                        className="bg-spectrum-orange-light/30 text-lc-warning border-lc-warning/30"
                      >
                        Ends {formattedCancelDate}
                      </Badge>
                    )}
                  </div>
                  {isSubscriptionLoading ? (
                    <div className="h-4 w-40 bg-muted animate-pulse rounded" />
                  ) : currentPlan ? (
                    <div className="space-y-1">
                      {!isEnterprisePlan && (
                        <p
                          data-testid="seat-count"
                          className="text-xs text-muted-foreground"
                        >
                          {seatCount} seat{seatCount > 1 ? "s" : ""}
                        </p>
                      )}
                      {includedCreditsPerSeat > 0 && (
                        <p className="text-xs text-muted-foreground">
                          {formatInteger(includedCreditsPerSeat)} credits
                          included per seat
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Free Plan - No subscription
                    </p>
                  )}
                </div>
              </div>
              {isSelfServeBilling && (
                <div className="flex justify-end items-center gap-2 lg:w-auto w-full">
                  {isScheduledForCancellation ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowReactivateDialog(true)}
                      disabled={cancelSubscription.isPending}
                    >
                      Reactivate Subscription
                    </Button>
                  ) : (
                    <Button
                      data-testid="change-plan-button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        trackBillingPlanChangeClicked({
                          currentPlan: currentPlanName,
                          source: "usage_billing",
                        });
                        navigate(ROUTES.BILLING.PLANS);
                      }}
                      disabled={isSubscriptionLoading}
                    >
                      Change Plan
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardHeader>
        </Card>

        {/* Credit Usage Section */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rust/10 to-spectrum-indigo/10 flex items-center justify-center shadow-sm">
              <div className="text-teal">
                <Activity className="h-6 w-6" />
              </div>
            </div>
            <div>
              <h2 className="text-xl font-semibold">Credit Usage</h2>
              <p className="text-sm text-muted-foreground">
                Overview of credit consumption for your current billing period
              </p>
            </div>
          </div>

          <Card className="overflow-hidden mb-8">
            <CardContent className="p-6">
              {/* Usage Progress Bar */}
              <UsageProgressBar className="mb-6" />
            </CardContent>
          </Card>

          {/* Usage Overview Pills */}
          <div
            className={`grid grid-cols-1 ${!isOnFreeTrial ? "md:grid-cols-2" : ""} gap-6 mb-8`}
          >
            <Card className="overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-spectrum-blue-light/15 dark:bg-spectrum-blue-dark/30/20 flex items-center justify-center">
                    <Coins className="h-7 w-7 text-teal dark:text-teal" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Total Credits Used
                    </p>
                    {isCreditUsageLoading ? (
                      <div className="h-8 w-24 bg-muted animate-pulse rounded" />
                    ) : (
                      <p className="text-3xl font-bold text-foreground">
                        {formatInteger(creditUsage?.data.total_count || 0)}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Only show On-Demand Usage for non-trial users */}
            {!isOnFreeTrial && (
              <Card className="overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-lc-active/10 dark:bg-spectrum-green-dark/30/20 flex items-center justify-center">
                      <CreditCard className="h-7 w-7 text-lc-active dark:text-lc-active" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        On-Demand Usage
                      </p>
                      {isCreditUsageLoading ? (
                        <div className="h-8 w-24 bg-muted animate-pulse rounded" />
                      ) : (
                        <p className="text-3xl font-bold text-foreground">
                          {formatCurrencyFromCents(
                            creditUsage?.data.total_billable_amount || 0,
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Usage by User Table */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Usage by User</h3>
              <p className="text-sm text-muted-foreground">
                Detailed breakdown of credit usage per team member
              </p>
            </CardHeader>
            <CardContent>
              {isCreditUsageLoading ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className="flex justify-between items-center py-3"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-muted animate-pulse rounded-full" />
                        <div className="space-y-1">
                          <div className="h-4 w-32 bg-muted animate-pulse rounded" />
                          <div className="h-3 w-24 bg-muted animate-pulse rounded" />
                        </div>
                      </div>
                      <div className="text-right space-y-1">
                        <div className="h-4 w-16 bg-muted animate-pulse rounded ml-auto" />
                        <div className="h-3 w-12 bg-muted animate-pulse rounded ml-auto" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : creditUsage?.data.results &&
                creditUsage.data.results.length > 0 ? (
                <div className="space-y-0">
                  {/* Table header */}
                  <div className="grid grid-cols-12 gap-4 pb-3 border-b text-xs font-medium text-muted-foreground">
                    <div className="col-span-5">User</div>
                    <div className="col-span-3">Usage Progress</div>
                    <div className="col-span-4 text-right">Usage Info</div>
                  </div>

                  {/* User rows */}
                  {creditUsage.data.results.map(
                    (user: UsageRecordSumResult, index: number) => (
                      <div
                        key={user.user_id}
                        className={`grid grid-cols-12 gap-4 items-center py-4 ${
                          index < creditUsage.data.results.length - 1
                            ? "border-b"
                            : ""
                        }`}
                      >
                        {/* Avatar & Name Column */}
                        <div className="col-span-5 flex items-center gap-3">
                          <Avatar>
                            <AvatarImage
                              src={getAvatarImage(user.user_email ?? "")}
                              alt={
                                user.user_name ||
                                user.user_email ||
                                "Unknown User"
                              }
                            />
                            <AvatarFallback>
                              {getInitials(
                                user.user_name || user.user_email || "Unknown",
                              )}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-sm truncate">
                              {user.user_name ||
                                user.user_email ||
                                "Unknown User"}
                            </p>
                            {user.user_name && user.user_email && (
                              <p className="text-xs text-muted-foreground truncate">
                                {user.user_email}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Usage Bar Column */}
                        <div className="col-span-3">
                          <UserProgressBar
                            totalCreditsUsed={user.count}
                            includedCredits={includedCreditsPerSeat}
                          />
                        </div>

                        {/* Usage Info Column */}
                        <div className="col-span-4 text-right">
                          <p className="font-medium text-sm">
                            {formatInteger(user.count)} credits
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {user.count > includedCreditsPerSeat
                              ? `${formatInteger(Math.min(user.count, includedCreditsPerSeat))} included + ${formatInteger(user.count - includedCreditsPerSeat)} over`
                              : `${formatInteger(user.count)} of ${formatInteger(includedCreditsPerSeat)} included`}
                          </p>
                        </div>
                      </div>
                    ),
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-12 h-12 rounded-xl bg-muted/50 flex items-center justify-center mx-auto mb-3">
                    <Coins className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium text-muted-foreground">
                    No usage data yet
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Credit usage will appear here once your team starts using
                    the platform
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* On-Demand Credit Budget Section - only show for active subscriptions.
              Beta orgs have a fixed, server-provisioned overage limit and the
              backend rejects updates with 403, so hide the form entirely. */}
        {currentPlan && !isOnFreeTrial && !isBetaPlan && (
          <Card>
            <CardHeader>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rust/10 to-plum/10 flex items-center justify-center">
                  <Settings className="h-6 w-6 text-teal" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">
                    On-Demand Credit Budget
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Set a budget amount to control your on-demand credit
                    spending per billing period
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                {/* Unlimited Budget Toggle */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">
                      Unlimited on-demand budget
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Allow unlimited on-demand credit spending
                    </p>
                  </div>
                  <Switch
                    checked={isUnlimitedBudget}
                    onCheckedChange={setIsUnlimitedBudget}
                  />
                </div>

                {/* Budget Amount Input */}
                <div
                  className={`space-y-2 transition-opacity duration-200 ${
                    isUnlimitedBudget ? "opacity-50 pointer-events-none" : ""
                  }`}
                >
                  <Label
                    htmlFor="budget-amount"
                    className="text-sm font-medium"
                  >
                    Budget amount
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                      $
                    </span>
                    <Input
                      id="budget-amount"
                      type="number"
                      min="0"
                      step="0.01"
                      value={budgetAmount}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setBudgetAmount(e.target.value)
                      }
                      placeholder="0"
                      className="pl-8"
                      disabled={isUnlimitedBudget}
                    />
                  </div>
                  {!isUnlimitedBudget &&
                    budgetAmountNumber >= 0 &&
                    budgetAmount !== "" && (
                      <p className="text-xs text-muted-foreground">
                        {budgetAmountNumber === 0
                          ? "No on-demand spending allowed"
                          : `≈ ${formatInteger(budgetCredits)} credits at $${(creditPrice / 100).toFixed(3)} per credit`}
                      </p>
                    )}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3">
                <Button
                  onClick={handleSaveBudget}
                  disabled={isBudgetSaving || updateSubscription.isPending}
                  isLoading={isBudgetSaving}
                  size="sm"
                  variant="outline"
                >
                  Save Budget
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Billing Information Section */}
        {currentPlan && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Billing Information</h2>
            <Card>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {/* Next Billing Date */}
                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-1">
                      <p className="font-medium">Next billing date</p>
                      {isSubscriptionLoading ? (
                        <div className="h-4 w-32 bg-muted animate-pulse rounded" />
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          {formattedBillingDate || "Not available"}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      {isSubscriptionLoading ? (
                        <div className="space-y-1">
                          <div className="h-5 w-16 bg-muted animate-pulse rounded ml-auto" />
                          <div className="h-4 w-20 bg-muted animate-pulse rounded ml-auto" />
                        </div>
                      ) : (
                        <>
                          <p className="font-medium">
                            {formatCurrencyFromCents(seatPrice * seatCount)}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            per {billingInterval === "year" ? "year" : "month"}
                          </p>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Payment Method */}
                  <div
                    data-testid="payment-method-section"
                    className="flex justify-between items-center pt-4 border-t"
                  >
                    <div className="flex flex-col gap-1">
                      <p className="font-medium">Payment method</p>
                      {isPaymentMethodsLoading ? (
                        <div className="h-4 w-40 bg-muted animate-pulse rounded" />
                      ) : paymentMethodDisplay ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          {paymentMethodDisplay.icon && (
                            <paymentMethodDisplay.icon width={30} />
                          )}
                          <span>{paymentMethodDisplay.text}</span>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          No payment method on file
                        </p>
                      )}
                    </div>
                    <Button
                      data-testid="add-payment-method-button"
                      variant="outline"
                      size="sm"
                      onClick={handleUpdatePaymentMethod}
                      disabled={
                        createSetupIntent.isPending || isPaymentMethodsLoading
                      }
                      isLoading={createSetupIntent.isPending}
                    >
                      {defaultPaymentMethod ? "Update" : "Add Payment Method"}
                    </Button>
                  </div>

                  {/* Billing Email */}
                  <div className="flex justify-between items-center pt-4 border-t">
                    <div className="flex flex-col gap-1">
                      <p className="font-medium">Billing email</p>
                      <p className="text-sm text-muted-foreground">
                        {currentPlan?.billing_email || "No billing email set"}
                      </p>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowBillingEmailDialog(true)}
                      disabled={isSubscriptionLoading}
                    >
                      {currentPlan?.billing_email
                        ? "Update"
                        : "Set Billing Email"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Invoices Section */}
        {currentPlan && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Billing History</h2>
            <Card>
              <CardContent className="p-6">
                {isInvoicesLoading ? (
                  <div className="space-y-3">
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className="flex justify-between items-center"
                      >
                        <div className="space-y-1">
                          <div className="h-4 w-32 bg-muted animate-pulse rounded" />
                          <div className="h-3 w-24 bg-muted animate-pulse rounded" />
                        </div>
                        <div className="space-y-1">
                          <div className="h-4 w-16 bg-muted animate-pulse rounded ml-auto" />
                          <div className="h-3 w-12 bg-muted animate-pulse rounded ml-auto" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : invoices?.data.stripe_invoices &&
                  invoices.data.stripe_invoices.length > 0 ? (
                  <div className="space-y-4">
                    {invoices.data.stripe_invoices.map((invoice, index) => (
                      <div
                        key={invoice.id}
                        className={`flex justify-between items-center ${
                          index > 0 ? "pt-4 border-t" : ""
                        }`}
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">
                              {new Date(invoice.created_at).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                },
                              )}
                            </p>
                            <Badge
                              variant={
                                invoice.status === "paid"
                                  ? "default"
                                  : "secondary"
                              }
                              className="text-xs"
                            >
                              {invoice.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Invoice {invoice.number}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="font-medium">
                              {formatCurrencyFromCents(invoice.total)}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                trackBillingInvoiceViewed({
                                  invoiceId: invoice.id,
                                  invoiceAmount: invoice.total,
                                  invoiceStatus: invoice.status,
                                });
                                window.open(invoice.hosted_url, "_blank");
                              }}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                trackBillingInvoiceDownloaded({
                                  invoiceId: invoice.id,
                                  invoiceAmount: invoice.total,
                                  invoiceStatus: invoice.status,
                                });
                                window.open(invoice.download_url, "_blank");
                              }}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 rounded-xl bg-muted/50 flex items-center justify-center mx-auto mb-3">
                      <CreditCard className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <p className="text-sm font-medium text-muted-foreground">
                      No invoices yet
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Your billing history will appear here once you're charged
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Plan Reactivation Section - Show for scheduled cancellations */}
        {currentPlan && isScheduledForCancellation && isSelfServeBilling && (
          <div>
            <Card className="border-lc-warning/30 dark:border-orange-800 bg-lc-warning/10/50 dark:bg-spectrum-orange-dark/30/10">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-orange-900 dark:text-orange-100">
                      Subscription Scheduled for Cancellation
                    </h3>
                    <p className="text-sm text-lc-warning dark:text-lc-warning mt-1 mb-4">
                      Your subscription is scheduled to end on{" "}
                      {formattedCancelDate}. After this date, your account will
                      be downgraded to the free plan. You can reactivate your
                      subscription at any time before then.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowReactivateDialog(true)}
                      disabled={cancelSubscription.isPending}
                      className="border-lc-warning/30 text-lc-warning hover:bg-spectrum-orange-light/30 dark:border-orange-800 dark:text-orange-100 dark:hover:bg-orange-900/20"
                    >
                      Reactivate Subscription
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Plan Cancellation Section */}
        {currentPlan &&
          subscriptionStatus !== "canceled" &&
          !isScheduledForCancellation &&
          isSelfServeBilling && (
            <div>
              <Card className="border-lc-blocked/30 dark:border-spectrum-red-dark">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-red-900 dark:text-red-100">
                        Cancel Subscription
                      </h3>
                      <p className="text-sm text-red-700 dark:text-red-300 mt-1 mb-4">
                        Cancel your subscription and downgrade to the free plan.
                        You'll continue to have access to your current plan
                        features until {formattedBillingDate}.
                      </p>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setShowCancelDialog(true)}
                        disabled={cancelSubscription.isPending}
                      >
                        Cancel Subscription
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
      </div>

      {/* Cancel Subscription Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-lc-blocked" />
              Cancel Subscription
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel your subscription?
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <h4 className="font-medium text-sm">
                What happens when you cancel:
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>
                  • Your subscription will be cancelled at the end of your
                  current billing period
                </li>
                <li>
                  • You'll continue to have access to all features until{" "}
                  {formattedBillingDate}
                </li>
                <li>
                  • After {formattedBillingDate}, your account will be
                  downgraded to the free plan
                </li>
                <li>
                  • Your data will be preserved and you can reactivate anytime
                </li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCancelDialog(false)}
              disabled={cancelSubscription.isPending}
            >
              Keep Subscription
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelSubscription}
              disabled={cancelSubscription.isPending}
              isLoading={cancelSubscription.isPending}
            >
              Cancel Subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reactivate Subscription Dialog */}
      <Dialog
        open={showReactivateDialog}
        onOpenChange={setShowReactivateDialog}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              Reactivate Subscription
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to reactivate your subscription?
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <h4 className="font-medium text-sm">
                What happens when you reactivate:
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Your subscription cancellation will be cancelled</li>
                <li>
                  • You'll continue to have access to all features beyond{" "}
                  {formattedCancelDate}
                </li>
                <li>
                  • Your billing will resume normally on {formattedBillingDate}
                </li>
                <li>• You can cancel again anytime if needed</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowReactivateDialog(false)}
              disabled={cancelSubscription.isPending}
            >
              Keep Cancelled
            </Button>
            <Button
              onClick={handleReactivateSubscription}
              disabled={cancelSubscription.isPending}
              isLoading={cancelSubscription.isPending}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              Reactivate Subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan Configuration Modal */}
      {selectedPlan && (
        <PlanConfigurationModal
          plan={selectedPlan}
          open={showPlanModal}
          buttonLoading={updateSubscription.isPending}
          redirectTo={ROUTES.SETTINGS.USAGE_BILLING}
          onComplete={handlePlanConfigurationComplete}
          onClose={() => setShowPlanModal(false)}
        />
      )}

      {/* Billing Email Dialog */}
      <Dialog
        open={showBillingEmailDialog}
        onOpenChange={setShowBillingEmailDialog}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Billing Email</DialogTitle>
            <DialogDescription>
              This email will be used for invoices and billing notifications.
            </DialogDescription>
          </DialogHeader>

          <Form {...billingEmailForm}>
            <form
              onSubmit={billingEmailForm.handleSubmit(handleUpdateBillingEmail)}
              className="space-y-4"
            >
              <FormField
                control={billingEmailForm.control}
                name="billing_email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email address</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="billing@company.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowBillingEmailDialog(false)}
                  disabled={updateSubscription.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updateSubscription.isPending}
                  isLoading={updateSubscription.isPending}
                >
                  Update Email
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UsageBilling;
